/**
 * Flux unique : stratégie (révision + session) → recherche question → affichage.
 * Si aucun QCM ne correspond : message constant NO_QUESTION_FOR_STRATEGY_MESSAGE.
 */
import { SEARCH_QCM_LAB_API_BASE_URL, authHeaders, fetchNextQCMByStrategy } from '../services/api';
import {
    aggregateMakeupConceptsByTriplet,
    collectAllSourceQuestionIds,
    pickHighestMakeupSession,
    loadMakeupSessionsForExamSession,
} from './makeupSessionStorage';
import { triggerDashboardCompute } from './dashboardApi';

/** Message affiché quand la recherche ne retourne aucune question pour la stratégie active */
export const NO_QUESTION_FOR_STRATEGY_MESSAGE = 'no question found for this strategy';

/** Aligné sur MobileQCM (filtres de révision). */
export const DEFAULT_REVISION_STRATEGY = {
    filterType: 'none',
    granularKeys: [],
    revisionTags: [],
    domainSelection: [],
    taskSelection: [],
    keywordSelection: [],
    studyMode: 'qcm',
};

export function resolveRevisionStrategyStorageKey(userId, examId, sessionId) {
    const sid = userId || 'anonymous_user';
    const ex = String(examId || '').trim() || 'exam';
    const ss = String(sessionId || '').trim() || 'default';
    return `revision_strategy_${sid}__${ex}__${ss}`;
}

function resolveLegacyRevisionStrategyStorageKey(userId) {
    return `revision_strategy_${userId || 'anonymous_user'}`;
}

export function readRevisionStrategyFromStorage(userId, examId, sessionId) {
    const scopedKey = resolveRevisionStrategyStorageKey(userId, examId, sessionId);
    const legacyKey = resolveLegacyRevisionStrategyStorageKey(userId);
    try {
        const scopedRaw = localStorage.getItem(scopedKey);
        if (scopedRaw) {
            const parsed = JSON.parse(scopedRaw);
            return typeof parsed === 'object' && parsed ? parsed : null;
        }
        // Migration unique : ancienne clé « par utilisateur » → clé user + examen + session.
        const legacyRaw = localStorage.getItem(legacyKey);
        if (!legacyRaw) return null;
        const parsed = JSON.parse(legacyRaw);
        if (typeof parsed !== 'object' || !parsed) return null;
        localStorage.setItem(scopedKey, legacyRaw);
        localStorage.removeItem(legacyKey);
        return parsed;
    } catch {
        return null;
    }
}

/** État initial synchrone (même lecture que le routeur pour le 1er QCM). */
export function getMergedRevisionStrategyFromStorage(userId, examId, sessionId) {
    const raw = readRevisionStrategyFromStorage(userId, examId, sessionId);
    return { ...DEFAULT_REVISION_STRATEGY, ...(raw || {}) };
}

export function saveRevisionStrategyToStorage(userId, examId, sessionId, next) {
    const scopedKey = resolveRevisionStrategyStorageKey(userId, examId, sessionId);
    localStorage.setItem(scopedKey, JSON.stringify(next));
    try {
        localStorage.removeItem(resolveLegacyRevisionStrategyStorageKey(userId));
    } catch (_) {
        /* ignore */
    }
}

/** Type Mongo/API pour les appels revision_qcm et next-by-strategy selon le mode d'étude. */
export function revisionItemQuestionTypeFromMerged(merged) {
    return merged?.studyMode === 'flashcard' ? 'FlashCard_Tips' : 'qcm';
}

/** Si plus aucun sous-domaine « sous-pratiqué », repasse le filtre de révision sur Random. */
export function applyRandomRevisionStrategyWhenLeftTopicsExhausted(userId, examId, sessionId) {
    try {
        const o = readRevisionStrategyFromStorage(userId, examId, sessionId) || {};
        const next = { ...DEFAULT_REVISION_STRATEGY, ...o, filterType: 'random' };
        saveRevisionStrategyToStorage(userId, examId, sessionId, next);
    } catch (_) {
        /* ignore */
    }
}

/** Shown once per user+exam when left_topics falls back to Random (≥3 questions on all sub-domains). */
const leftTopicsRandomFallbackNoticeStorageKey = (userId, examId) =>
    `left_topics_random_fallback_notice_shown_${userId || 'anonymous_user'}_${examId || 'exam'}`;

export function shouldShowLeftTopicsRandomFallbackNotice(userId, examId) {
    try {
        return localStorage.getItem(leftTopicsRandomFallbackNoticeStorageKey(userId, examId)) !== '1';
    } catch (_) {
        return true;
    }
}

export function markLeftTopicsRandomFallbackNoticeShown(userId, examId) {
    try {
        localStorage.setItem(leftTopicsRandomFallbackNoticeStorageKey(userId, examId), '1');
    } catch (_) {
        /* ignore */
    }
}

/** Same copy for sessionStorage + in-app banner; detect variants from API/context. */
export const LEFT_TOPICS_RANDOM_FALLBACK_NOTICE_EN =
    'Under-practiced topics: all catalog sub-domains already have at least 3 questions. Revision filter set to Random.';

export function isLeftTopicsRandomFallbackNoticeText(message) {
    const s = String(message || '').toLowerCase();
    return (
        s.includes('revision filter set to random') ||
        (s.includes('random') &&
            (s.includes('under-practiced') || s.includes('sub-domain') || s.includes('subdomains')))
    );
}

function isValidTaskForRevision(task) {
    const s = String(task || '').trim();
    if (!s) return false;
    const low = s.toLowerCase();
    return low !== '(no task)' && low !== 'no task' && low !== '(unknown)' && low !== 'unknown';
}

/**
 * Premier QCM pour la stratégie under-practiced (tasks avec asked_count &lt; 3 dans le catalogue).
 * @returns {string|null} qcm_id ou null
 */
export async function pickQcmIdForUnderpracticedTopics({
    examId,
    userId,
    sessionId,
    excludeQcmId = null,
    questionType = 'qcm',
}) {
    try {
        const makeup = loadMakeupSessionsForExamSession(userId, examId, sessionId);
        const dash = await triggerDashboardCompute(userId, examId, makeup, sessionId);
        const rows = Array.isArray(dash?.subdomain_catalog_progress)
            ? dash.subdomain_catalog_progress
            : [];
        const uncoveredRows = rows.filter((r) => {
            const asked = Number(r?.asked_count || 0);
            const exp = Number(r?.expected_count);
            const target = Number.isFinite(exp) && exp > 0 ? exp : 3;
            return asked < target;
        });
        const tasks = [
            ...new Set(
                uncoveredRows
                    .map((r) => String(r.task || '').trim())
                    .filter((t) => isValidTaskForRevision(t))
            ),
        ];
        if (!tasks.length) return null;

        // 1) Cible prioritaire: sous-domaines uncovered (Task strict)
        if (tasks.length) {
            const data = await postRevisionQcm(
                examId,
                userId,
                {
                    filter_type: 'manual_task',
                    selection: tasks,
                    question_type: questionType,
                    exclude_qcm_id: excludeQcmId || undefined,
                },
                { includeExclusions: true }
            );
            if (data.success && data.results?.length) {
                return String(data.results[0].id);
            }
        }

        // Pas de fallback domaine ni relâché: left_topics reste strictement sous-domaine.
    } catch (e) {
        console.warn('pickQcmIdForUnderpracticedTopics:', e?.message || e);
    }
    return null;
}

export async function postRevisionQcm(examId, userId, body, opts = {}) {
    const includeExclusions = opts.includeExclusions !== false;
    const excludeIds = includeExclusions ? collectAllSourceQuestionIds(userId) : [];
    const payload = {
        exam_id: examId,
        ...body,
    };
    const uid = String(userId || '').trim();
    if (uid) {
        payload.userid = payload.userid || uid;
    }
    const sid = String(opts.sessionId || '').trim();
    if (sid && !payload.session_id) {
        payload.session_id = sid;
    }
    const cur = String(opts.excludeQcmId || '').trim();
    if (cur && !payload.exclude_qcm_id) {
        payload.exclude_qcm_id = cur;
    }
    if (includeExclusions) {
        payload.exclude_qcm_ids = [...new Set(excludeIds)];
    }
    const resp = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/revision_qcm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    return resp.json();
}

/**
 * Premier QCM issu de la session make-up (même logique que MobileQCM).
 * @returns {{ qcmId: string, makeupSessionId: string } | null}
 */
export async function fetchMakeupQcmIdFromTopSession(examId, userId) {
    const top = pickHighestMakeupSession(userId);
    if (!top) return null;

    const keywords = top.content?.keywords || [];
    const domain = top.metadata?.domain || '';
    const task = top.metadata?.task || '';
    const exclude = top.source_question_id;
    const excludeIds = collectAllSourceQuestionIds(userId);
    const sessionId = top['make-up_session_id'];

    const fetchMakeup = async (d, t) => {
        const resp = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/makeup_qcm`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
                userid: userId,
                exam_id: examId,
                keywords,
                exclude_qcm_id: exclude,
                exclude_qcm_ids: excludeIds,
                domain: d,
                task: t,
            }),
        });
        return resp.json();
    };

    try {
        let data = await fetchMakeup(domain, task);
        if ((!data.success || !data.results?.length) && (domain || task)) {
            data = await fetchMakeup('', '');
        }
        if (data.success && data.results?.length) {
            const best =
                data.results.find((r) => String(r.id) !== String(exclude)) || data.results[0];
            return { qcmId: String(best.id), makeupSessionId: sessionId };
        }
    } catch (e) {
        console.warn('makeup_qcm (initial) failed:', e?.message || e);
    }
    return null;
}

const fail = () => ({ qcmId: null, error: NO_QUESTION_FOR_STRATEGY_MESSAGE });

/**
 * Résout le **premier** qcm_id : stratégie de révision (si active) puis stratégie de session (Node).
 * Pas de repli vers un autre examen ni QCM « au hasard » si la stratégie impose un filtre.
 *
 * @returns {{ qcmId: string|null, error: string|null }}
 */
export async function resolveFirstQcmId({ examId, userId, sessionStrategy, sessionId }) {
    if (!examId) return fail();

    const uid = String(userId || '').trim();
    const sid = sessionId || '111222';
    const isGuestUser = uid === 'guest' || uid.startsWith('guest_');
    if (isGuestUser) {
        try {
            const merged = getMergedRevisionStrategyFromStorage(uid, examId, sid);
            const itemQt = revisionItemQuestionTypeFromMerged(merged);
            const qcmId = await fetchNextQCMByStrategy(
                examId,
                sessionStrategy || 'random',
                uid || 'guest',
                itemQt
            );
            if (qcmId) {
                return { qcmId: String(qcmId), error: null };
            }
        } catch (e) {
            console.warn('resolveFirstQcmId guest path failed:', e?.message || e);
        }
        return fail();
    }

    const merged = getMergedRevisionStrategyFromStorage(userId, examId, sid);
    const itemQt = revisionItemQuestionTypeFromMerged(merged);

    if (merged.filterType === 'left_topics') {
        const picked = await pickQcmIdForUnderpracticedTopics({
            examId,
            userId,
            sessionId: sid,
            questionType: itemQt,
        });
        if (picked) {
            return { qcmId: picked, error: null };
        }
        applyRandomRevisionStrategyWhenLeftTopicsExhausted(userId, examId, sid);
        if (shouldShowLeftTopicsRandomFallbackNotice(userId, examId)) {
            try {
                sessionStorage.setItem(
                    'revision_strategy_notice',
                    JSON.stringify({ message: LEFT_TOPICS_RANDOM_FALLBACK_NOTICE_EN })
                );
            } catch (_) {
                /* ignore */
            }
        }
        try {
            const qcmId = await fetchNextQCMByStrategy(examId, sessionStrategy, userId, itemQt);
            if (qcmId) {
                return { qcmId: String(qcmId), error: null };
            }
        } catch (e) {
            console.warn('resolveFirstQcmId left_topics fallback Random failed:', e?.message || e);
        }
        return fail();
    }

    if (merged.filterType === 'manual_task' && merged.taskSelection?.length > 0) {
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'manual_task',
                selection: merged.taskSelection,
                question_type: itemQt,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (merged.filterType === 'manual_domain' && merged.domainSelection?.length > 0) {
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'manual_domain',
                selection: merged.domainSelection,
                question_type: itemQt,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (merged.filterType === 'unseen_qcm') {
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'unseen_qcm',
                question_type: itemQt,
                userid: userId,
                session_id: sid,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (
        merged.filterType === 'keywords_fuzzy' &&
        Array.isArray(merged.keywordSelection) &&
        merged.keywordSelection.length > 0
    ) {
        const selection = merged.keywordSelection
            .map((k) => String(k || '').trim())
            .filter(Boolean);
        if (!selection.length) {
            return fail();
        }
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'keywords_fuzzy',
                selection,
                question_type: itemQt,
                userid: userId,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (merged.filterType === 'weakness_granular' && merged.granularKeys?.length > 0) {
        const concepts = aggregateMakeupConceptsByTriplet(userId);
        const tags = merged.granularKeys
            .map((k) => {
                const c = concepts.find((x) => x.key === k);
                if (!c) return null;
                const raw = c.searchTags || c.keywords || [];
                const cleaned = raw.filter(
                    (x) => x && typeof x === 'string' && !String(x).startsWith('(')
                );
                return cleaned.length ? [...cleaned] : null;
            })
            .filter(Boolean);
        if (tags.length === 0) {
            return fail();
        }
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'weakness_granular',
                tags,
                question_type: itemQt,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (merged.filterType === 'weakness_granular_tags' && Array.isArray(merged.revisionTags) && merged.revisionTags.length > 0) {
        const tags = merged.revisionTags.filter((t) => Array.isArray(t) && t.some(Boolean));
        if (!tags.length) {
            return fail();
        }
        const data = await postRevisionQcm(
            examId,
            userId,
            {
                filter_type: 'weakness_granular',
                tags,
                question_type: itemQt,
            },
            { sessionId: sid }
        );
        if (data.success && data.results?.length) {
            return { qcmId: String(data.results[0].id), error: null };
        }
        return fail();
    }

    if (merged.filterType === 'weakness_all') {
        const makeup = await fetchMakeupQcmIdFromTopSession(examId, userId);
        if (makeup?.qcmId) {
            return { qcmId: makeup.qcmId, error: null };
        }
        return fail();
    }

    try {
        const qcmId = await fetchNextQCMByStrategy(examId, sessionStrategy, userId, itemQt);
        if (qcmId) {
            return { qcmId: String(qcmId), error: null };
        }
    } catch (e) {
        console.warn('resolveFirstQcmId Node strategy failed:', e?.message || e);
    }
    return fail();
}
